import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import ClipLoader from "react-spinners/ClipLoader"; // Add a loading spinner
import { FaMapMarkerAlt, FaFish } from "react-icons/fa"; // Import icons

const FishingMap = () => {
  const [locations, setLocations] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [radius, setRadius] = useState(50);
  const [useGps, setUseGps] = useState(true);
  const [zipCode, setZipCode] = useState("");
  const [coordinates, setCoordinates] = useState({ lat: null, lon: null });
  const [gpsError, setGpsError] = useState("");
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(""); // Error state
  const [mapCenter, setMapCenter] = useState([47.5, -120.5]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const markersRef = useRef([]); // Reference to store markers
  const mapRef = useRef(); // Reference to the map

  useEffect(() => {
    if (useGps && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoordinates({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
          setGpsError("");
        },
        (error) => {
          console.error("Error getting GPS coordinates:", error);
          setGpsError("Unable to retrieve GPS coordinates. Please enter a zip code.");
        }
      );
    }
  }, [useGps]);

  const fetchLocations = async () => {
    if (!date) {
      console.log("No date selected. API call blocked.");
      return;
    }

    let lat, lon;
    if (useGps) {
      if (!coordinates.lat || !coordinates.lon) {
        console.log("No GPS coordinates available. API call blocked.");
        return;
      }
      lat = coordinates.lat;
      lon = coordinates.lon;
    } else {
      const zipApiUrl = `http://api.zippopotam.us/us/${zipCode}`;
      try {
        const zipResponse = await fetch(zipApiUrl);
        if (!zipResponse.ok) throw new Error(`Zip code API request failed with status: ${zipResponse.status}`);
        const zipData = await zipResponse.json();
        lat = zipData.places[0].latitude;
        lon = zipData.places[0].longitude;
      } catch (error) {
        console.error("Error fetching zip code coordinates:", error);
        setError("Failed to fetch coordinates for the provided zip code."); 
        return;
      }
    }

    //const apiUrl = `https://ah-fishing-proxy100-fhbeaqergnfkg0ab.westus2-01.azurewebsites.net/api/fishing-locations?lat=${lat}&lon=${lon}&date=${date}&radius=${radius}`;
    const apiUrl = `https://alarminghornetfishingapi.blackisland-5c24bcff.westus2.azurecontainerapps.io/api/fishing-locations?lat=${lat}&lon=${lon}&date=${date}&radius=${radius}`;
    console.log("Fetching locations from:", apiUrl);

    setLoading(true); // Start loading
    setError(""); // Reset error

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        const bodyText = await response.text().catch(() => "<unreadable body>");
        console.error("API error response:", {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: bodyText,
        });
        throw new Error(`API request failed with status: ${response.status} - ${bodyText}`);
      }

      const data = await response.json();
      console.log("API response received:", data);

      const locationsArray = Object.values(data);
      const validLocations = locationsArray.filter(loc => loc.latitude && loc.longitude);
      console.log("Filtered valid locations:", validLocations);

      const sortedLocations = validLocations.sort((a, b) => b.overall_score - a.overall_score);
      setLocations(sortedLocations);

      // Update map center
      setMapCenter([lat, lon]);
      setSearchPerformed(true);

      // Calculate bounds and fit map to bounds with padding
      const bounds = L.latLng([lat, lon]).toBounds(radius * 1609.34 * 2); // Convert miles to meters and create bounds
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds, { padding: [5, 5] }); // Add padding to ensure the circle fits within the view
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Surface the server error message in the UI for debugging (safe for dev).
      setError(`Failed to fetch fishing locations. ${error && error.message ? error.message : "Please try again later."}`);
    } finally {
      setLoading(false); // End loading
    }
  };

  const getIconColor = (score) => {
    if (score >= 70) return "green";
    if (score <= 31) return "red";
    return "yellow";
  };

  const createCustomIcon = (color) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="6" fill="${color}" /></svg>`;
    const url = `data:image/svg+xml;base64,${btoa(svg)}`;
    return new L.Icon({
      iconUrl: url,
      iconSize: [12, 12],
      className: 'custom-icon'
    });
  };

  const currentLocationIcon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/252/252025.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  const handleRowClick = (index) => {
    const marker = markersRef.current[index];
    if (marker) {
      marker.openPopup();
    }
  };

  const createInvertedCircle = (center, radius) => {
    const earthRadius = 6378137; // Earth's radius in meters
    const lat = center[0];
    const lon = center[1];
    const dLat = radius / earthRadius;
    const dLon = radius / (earthRadius * Math.cos(Math.PI * lat / 180));

    const outerBounds = [
      [90, -180],
      [90, 180],
      [-90, 180],
      [-90, -180],
    ];

    const innerBounds = [];
    for (let i = 0; i <= 360; i += 10) {
      const angle = i * Math.PI / 180;
      const innerLat = lat + (dLat * Math.sin(angle)) * 180 / Math.PI;
      const innerLon = lon + (dLon * Math.cos(angle)) * 180 / Math.PI;
      innerBounds.push([innerLat, innerLon]);
    }

    return [outerBounds, innerBounds];
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Fishing Locations in Washington</h1>

      <div className="flex gap-2 mb-4">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="p-2 border border-gray-300 rounded"
        />
        <input
          type="number"
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
          placeholder="Radius (miles)"
          className="p-2 border border-gray-300 rounded"
        />
        <button
          onClick={fetchLocations}
          disabled={!date}
          className={`p-2 rounded ${date ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-700 cursor-not-allowed"}`}
        >
          <FaFish className="inline mr-2" /> Search
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={useGps}
            onChange={() => setUseGps(true)}
          />
          <FaMapMarkerAlt className="inline mr-1" /> Use GPS
        </label>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={!useGps}
            onChange={() => setUseGps(false)}
          />
          <FaMapMarkerAlt className="inline mr-1" /> Enter Zip Code
        </label>
        {!useGps && (
          <input
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="Zip Code"
            className="p-2 border border-gray-300 rounded"
          />
        )}
      </div>

      {gpsError && <p className="text-red-500">{gpsError}</p>}
      {error && (
        <div className="mb-2">
          <p className="text-red-500">{error}</p>
          <button
            onClick={fetchLocations}
            className="mt-2 p-2 bg-yellow-500 text-white rounded"
          >
            Retry
          </button>
        </div>
      )}

      <p>Selected Date: {date}</p>
      <p>Selected Radius: {radius} miles</p>

      <div className="map-container border border-gray-300 shadow-lg" style={{ height: "800px" }}>
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <ClipLoader size={50} color={"#123abc"} loading={loading} />
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={7}
            style={{ height: "100%" }}
            scrollWheelZoom={false}
            whenCreated={(mapInstance) => { mapRef.current = mapInstance; }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {coordinates.lat && coordinates.lon && searchPerformed && (
              <>
                <Marker position={[coordinates.lat, coordinates.lon]} icon={currentLocationIcon}>
                  <Popup>
                    <strong>Your Location</strong>
                  </Popup>
                </Marker>
                <Polygon
                  positions={createInvertedCircle([coordinates.lat, coordinates.lon], radius * 1609.34)}
                  color="gray"
                  fillColor="gray"
                  fillOpacity={0.2}
                />
              </>
            )}

            {locations.length > 0 ? (
              locations.map((loc, index) => (
                <Marker
                  key={index}
                  position={[loc.latitude, loc.longitude]}
                  icon={createCustomIcon(getIconColor(loc.overall_score))}
                  ref={(el) => (markersRef.current[index] = el)}
                >
                  <Popup>
                    <strong>{loc.name}</strong>
                    <br />
                    <strong>Description:</strong> {loc.description}
                    <br />
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`} target="_blank" rel="noopener noreferrer">
                      Get Directions
                    </a>
                    <br />
                    <strong>Weather Forecast:</strong>
                    <ul>
                      {loc.weather_info.slice(0, 2).map((forecast, idx) => (
                        <li key={idx}>
                          <strong>{forecast.is_daytime ? "Day" : "Night"}</strong>: {forecast.short_forecast}, {forecast.temperature}°{forecast.temperature_unit}, Wind: {forecast.wind_speed} {forecast.wind_direction}
                        </li>
                      ))}
                    </ul>
                    {loc.fishing_season_info.length > 0 && (
                      <>
                        <strong>Fishing Prospects:</strong>
                        <ul>
                          {loc.fishing_season_info.map((info, idx) => (
                            <li key={idx}>
                              {info.fish_type}: {info.fishing_quality}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {loc.stocking_info.length > 0 && (
                      <>
                        <strong>Recent Stocking Report:</strong>
                        <ul>
                          {loc.stocking_info.slice(0, 1).map((report, idx) => (
                            <li key={idx}>
                              {report.species}: {report.number} on {new Date(report.stock_date).toLocaleDateString()}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Popup>
                </Marker>
              ))
            ) : (
              <p className="text-gray-500">No locations found.</p>
            )}
          </MapContainer>
        )}
      </div>

      <div className="mt-4">
        <h2 className="text-lg font-bold mb-2">Top Fishing Locations</h2>
        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b">Location</th>
              <th className="py-2 px-4 border-b">Overall Score</th>
              <th className="py-2 px-4 border-b">County</th>
              <th className="py-2 px-4 border-b">Water Body Type</th>
              <th className="py-2 px-4 border-b">Weather</th>
              <th className="py-2 px-4 border-b">Stocking</th>
              <th className="py-2 px-4 border-b">Fishing Prospects</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc, index) => (
              <tr
                key={index}
                className={`hover:bg-gray-100 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                onClick={() => handleRowClick(index)}
              >
                <td className="py-2 px-4 border-b border-gray-300">{loc.name}</td>
                <td className="py-2 px-4 border-b border-gray-300">{loc.overall_score}</td>
                <td className="py-2 px-4 border-b border-gray-300">{loc.county}</td>
                <td className="py-2 px-4 border-b border-gray-300">{loc.water_body_type}</td>
                <td className="py-2 px-4 border-b border-gray-300">
                  {loc.weather_info.slice(0, 2).map((forecast, idx) => (
                    <div key={idx}>
                      <strong>{forecast.is_daytime ? "Day" : "Night"}</strong>: {forecast.short_forecast}, {forecast.temperature}°{forecast.temperature_unit}, Wind: {forecast.wind_speed} {forecast.wind_direction}
                    </div>
                  ))}
                </td>
                <td className="py-2 px-4 border-b border-gray-300">
                  {loc.stocking_info.length > 0 ? (
                    loc.stocking_info.slice(0, 1).map((report, idx) => (
                      <div key={idx}>
                        {report.species}: {report.number} on {new Date(report.stock_date).toLocaleDateString()}
                      </div>
                    ))
                  ) : (
                    <div>No recent stocking</div>
                  )}
                </td>
                <td className="py-2 px-4 border-b border-gray-300">
                  {loc.fishing_season_info.length > 0 ? (
                    loc.fishing_season_info.map((info, idx) => (
                      <div key={idx}>
                        {info.fish_type}: {info.fishing_quality}
                      </div>
                    ))
                  ) : (
                    <div>No fishing prospects</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FishingMap;